import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  startOfWeek, endOfWeek, addDays, addWeeks, subWeeks,
  format, isToday, isSameDay, parseISO
} from 'date-fns';
import { es, enUS, de } from 'date-fns/locale';
import { useI18n } from '@/contexts/I18nContext';
import { ChevronLeft, ChevronRight, Clock, Users, CalendarDays, Filter, GripVertical } from 'lucide-react';

const DATE_LOCALES = { es, en: enUS, de };

const HOUR_HEIGHT = 64;
const START_HOUR = 8;
const END_HOUR = 19;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);
const SNAP_MINUTES = 15;

const CATEGORY_BG = {
  arte: { bg: '#F3E8FF', border: '#844E33', text: '#7C3AED', label: 'Arte' },
  manualidades: { bg: '#FEF3C7', border: '#A8752B', text: '#A8752B', label: 'Manualidades' },
  agua: { bg: '#E0F2FE', border: '#38BDF8', text: '#0369A1', label: 'Agua' },
  cocina: { bg: '#FFEDD5', border: '#FB923C', text: '#C2410C', label: 'Cocina' },
  deporte: { bg: '#DCFCE7', border: '#4ADE80', text: '#15803D', label: 'Deporte' },
  naturaleza: { bg: '#D1FAE5', border: '#34D399', text: '#065F46', label: 'Naturaleza' },
  musica: { bg: '#FCE7F3', border: '#F472B6', text: '#BE185D', label: 'Musica' },
  general: { bg: '#F3F4F6', border: '#9CA3AF', text: '#374151', label: 'General' },
};

const ALL_CATEGORIES = Object.keys(CATEGORY_BG);

function getTimeOffset(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return (h - START_HOUR) * HOUR_HEIGHT + (m / 60) * HOUR_HEIGHT;
}

function getBlockHeight(startTime, endTime) {
  return getTimeOffset(endTime) - getTimeOffset(startTime);
}

function snapToGrid(minutes) {
  return Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;
}

function offsetToTime(yOffset) {
  const totalMinutes = (yOffset / HOUR_HEIGHT) * 60;
  const snapped = snapToGrid(totalMinutes);
  const h = Math.floor(snapped / 60) + START_HOUR;
  const m = snapped % 60;
  return `${String(Math.min(h, END_HOUR - 1)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function getDurationMinutes(start, end) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
}

function addMinutesToTime(time, mins) {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + mins;
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${String(Math.min(nh, END_HOUR)).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

function WorkshopBlock({ workshop, onClick, isReserved, draggable: canDrag, onDragStart: onDragStartProp }) {
  const cat = CATEGORY_BG[workshop.category] || CATEGORY_BG.general;
  const top = getTimeOffset(workshop.time_start);
  const height = Math.max(getBlockHeight(workshop.time_start, workshop.time_end), 32);

  const handleDragStart = (e) => {
    if (!canDrag) return;
    e.dataTransfer.setData('application/json', JSON.stringify({
      id: workshop.id,
      duration: getDurationMinutes(workshop.time_start, workshop.time_end),
    }));
    e.dataTransfer.effectAllowed = 'move';
    if (onDragStartProp) onDragStartProp(workshop);
  };

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick?.(workshop); }}
      draggable={canDrag}
      onDragStart={handleDragStart}
      className={`absolute left-1 right-1 rounded-xl px-2 py-1 overflow-hidden transition-all duration-200 hover:shadow-md hover:z-20 ${
        canDrag ? 'cursor-grab active:cursor-grabbing hover:scale-[1.03]' : 'cursor-pointer hover:scale-[1.02]'
      }`}
      style={{
        top: `${top}px`,
        height: `${height}px`,
        backgroundColor: cat.bg,
        borderLeft: `3px solid ${cat.border}`,
      }}
      data-testid={`calendar-workshop-${workshop.id}`}
    >
      <div className="flex items-start gap-1">
        {canDrag && <GripVertical size={10} style={{ color: cat.text }} className="opacity-40 mt-0.5 flex-shrink-0" />}
        <p className="font-heading font-bold text-xs leading-tight truncate flex-1" style={{ color: cat.text }}>
          {workshop.title}
        </p>
      </div>
      {height > 48 && (
        <p className="text-[10px] mt-0.5 opacity-70" style={{ color: cat.text }}>
          {workshop.time_start} - {workshop.time_end}
        </p>
      )}
      {height > 70 && (
        <div className="flex items-center gap-1 mt-0.5">
          <Users size={9} style={{ color: cat.text }} className="opacity-60" />
          <span className="text-[10px] opacity-60" style={{ color: cat.text }}>
            {workshop.current_count}/{workshop.max_capacity}
          </span>
          {isReserved && (
            <span className="text-[9px] font-bold px-1 rounded-full bg-white/60" style={{ color: cat.text }}>
              Reservado
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function WeeklyCalendar({ workshops, onSelectWorkshop, reservedIds = [], draggable = false, onWorkshopDrop }) {
  const { t, lang } = useI18n();
  const locale = DATE_LOCALES[lang] || es;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [mobileDay, setMobileDay] = useState(new Date());
  const [activeCategories, setActiveCategories] = useState(new Set(ALL_CATEGORIES));
  const [showFilters, setShowFilters] = useState(false);
  const [dropIndicator, setDropIndicator] = useState(null);
  const [draggingWorkshop, setDraggingWorkshop] = useState(null);
  const gridRef = useRef(null);

  const toggleCategory = (cat) => {
    setActiveCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) { next.delete(cat); } else { next.add(cat); }
      return next;
    });
  };

  const selectAllCategories = () => setActiveCategories(new Set(ALL_CATEGORIES));
  const clearAllCategories = () => setActiveCategories(new Set());

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const workshopsByDay = useMemo(() => {
    const map = {};
    days.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      map[dateStr] = (workshops || [])
        .filter(w => w.date === dateStr && activeCategories.has(w.category))
        .sort((a, b) => a.time_start.localeCompare(b.time_start));
    });
    return map;
  }, [workshops, days, activeCategories]);

  const goToToday = () => {
    setCurrentDate(new Date());
    setMobileDay(new Date());
  };

  const handleDragOver = useCallback((e, dateStr) => {
    if (!draggable) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const colEl = e.currentTarget;
    const rect = colEl.getBoundingClientRect();
    const yOffset = e.clientY - rect.top;
    const time = offsetToTime(yOffset);
    setDropIndicator({ dateStr, time, y: getTimeOffset(time) });
  }, [draggable]);

  const handleDrop = useCallback((e, dateStr) => {
    if (!draggable || !onWorkshopDrop) return;
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const colEl = e.currentTarget;
      const rect = colEl.getBoundingClientRect();
      const yOffset = e.clientY - rect.top;
      const newStart = offsetToTime(yOffset);
      const newEnd = addMinutesToTime(newStart, data.duration);
      onWorkshopDrop(data.id, { date: dateStr, time_start: newStart, time_end: newEnd });
    } catch (err) { console.error('Drop error:', err); }
    setDropIndicator(null);
    setDraggingWorkshop(null);
  }, [draggable, onWorkshopDrop]);

  const handleDragLeave = useCallback(() => {
    setDropIndicator(null);
  }, []);

  // Unique categories present in all workshops
  const presentCategories = useMemo(() => {
    const cats = new Set((workshops || []).map(w => w.category));
    return ALL_CATEGORIES.filter(c => cats.has(c));
  }, [workshops]);

  const totalHeight = HOURS.length * HOUR_HEIGHT;

  // Count workshops this week
  const weekWorkshopCount = days.reduce((sum, day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return sum + (workshopsByDay[dateStr]?.length || 0);
  }, 0);

  return (
    <div className="card-manrique p-0 overflow-hidden" data-testid="weekly-calendar">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 sm:p-5 border-b border-black/5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setCurrentDate(subWeeks(currentDate, 1)); }}
            className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#FBF8F3] transition-colors"
            data-testid="calendar-prev-week"
          >
            <ChevronLeft size={16} className="text-manrique-grey" />
          </button>
          <button
            onClick={() => { setCurrentDate(addWeeks(currentDate, 1)); }}
            className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#FBF8F3] transition-colors"
            data-testid="calendar-next-week"
          >
            <ChevronRight size={16} className="text-manrique-grey" />
          </button>
          <div>
            <h3 className="font-heading font-bold text-base sm:text-lg text-manrique-black">
              {format(weekStart, "d MMM", { locale })} - {format(addDays(weekStart, 6), "d MMM yyyy", { locale })}
            </h3>
            <p className="text-xs text-manrique-muted">
              {weekWorkshopCount} {weekWorkshopCount === 1 ? t('workshop_singular') : t('workshops_this_week')}
            </p>
          </div>
        </div>
        <button
          onClick={goToToday}
          className="text-sm font-medium text-[#48293A] hover:underline px-3 py-1.5 rounded-full border border-[#48293A]/20 hover:bg-[#48293A]/5 transition-colors"
          data-testid="calendar-today-button"
        >
          {t('today')}
        </button>
      </div>

      {/* Category Filters */}
      <div className="px-4 sm:px-5 py-3 border-b border-black/5 bg-[#FBF8F3]/50">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
              showFilters ? 'bg-[#48293A] text-white border-[#48293A]' : 'bg-white text-manrique-grey border-black/10 hover:border-black/20'
            }`}
            data-testid="toggle-category-filters"
          >
            <Filter size={12} /> {t('filter')}
            {activeCategories.size < ALL_CATEGORIES.length && (
              <span className="bg-white/20 px-1.5 rounded-full text-[10px]">{activeCategories.size}</span>
            )}
          </button>
          {showFilters && (
            <>
              <button onClick={selectAllCategories} className="text-[11px] text-[#48293A] hover:underline" data-testid="filter-select-all">{t('all')}</button>
              <button onClick={clearAllCategories} className="text-[11px] text-manrique-muted hover:underline" data-testid="filter-clear-all">{t('none')}</button>
              <div className="w-px h-4 bg-black/10" />
              {presentCategories.map(cat => {
                const c = CATEGORY_BG[cat];
                const active = activeCategories.has(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium transition-all border ${
                      active ? 'opacity-100' : 'opacity-40'
                    }`}
                    style={{
                      backgroundColor: active ? c.bg : 'transparent',
                      borderColor: c.border,
                      color: c.text,
                    }}
                    data-testid={`filter-category-${cat}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.border }} />
                    {c.label}
                  </button>
                );
              })}
            </>
          )}
          {!showFilters && activeCategories.size < ALL_CATEGORIES.length && (
            <span className="text-[11px] text-manrique-muted">
              {activeCategories.size} {t('categories_of')} {ALL_CATEGORIES.length} {t('categories')}
            </span>
          )}
        </div>
      </div>

      {/* Mobile day selector */}
      <div className="flex sm:hidden overflow-x-auto gap-1.5 px-4 py-3 border-b border-black/5 bg-[#FBF8F3]">
        {days.map(day => {
          const active = isSameDay(day, mobileDay);
          const today = isToday(day);
          const dateStr = format(day, 'yyyy-MM-dd');
          const count = workshopsByDay[dateStr]?.length || 0;
          return (
            <button
              key={dateStr}
              onClick={() => setMobileDay(day)}
              className={`flex flex-col items-center min-w-[3.2rem] px-2 py-2 rounded-2xl transition-all ${
                active ? 'bg-[#48293A] text-white shadow-lg' :
                today ? 'bg-[#48293A]/10 text-[#48293A]' :
                'text-manrique-grey hover:bg-white'
              }`}
              data-testid={`mobile-day-${dateStr}`}
            >
              <span className="text-[10px] font-medium uppercase">
                {format(day, 'EEE', { locale })}
              </span>
              <span className="text-lg font-bold leading-tight">{format(day, 'd')}</span>
              {count > 0 && (
                <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${active ? 'bg-white' : 'bg-[#48293A]'}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Desktop Week Grid */}
      <div className="hidden sm:block overflow-x-auto">
        <div className="min-w-[700px]">
          {/* Day headers */}
          <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-black/5">
            <div className="p-2" />
            {days.map(day => {
              const today = isToday(day);
              const dateStr = format(day, 'yyyy-MM-dd');
              return (
                <div
                  key={dateStr}
                  className={`text-center py-3 border-l border-black/5 ${today ? 'bg-[#48293A]/5' : ''}`}
                >
                  <p className="text-xs font-medium text-manrique-muted uppercase">
                    {format(day, 'EEE', { locale })}
                  </p>
                  <p className={`text-lg font-heading font-bold ${
                    today ? 'text-[#48293A]' : 'text-manrique-black'
                  }`}>
                    {format(day, 'd')}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Time grid */}
          <div ref={gridRef} className="grid grid-cols-[60px_repeat(7,1fr)] relative" style={{ height: totalHeight }}>
            {/* Hour labels */}
            <div className="relative">
              {HOURS.map(hour => (
                <div
                  key={hour}
                  className="absolute left-0 right-0 flex items-start justify-end pr-2"
                  style={{ top: (hour - START_HOUR) * HOUR_HEIGHT }}
                >
                  <span className="text-[11px] text-manrique-muted -mt-2 font-medium">
                    {String(hour).padStart(2, '0')}:00
                  </span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            {days.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const dayWorkshops = workshopsByDay[dateStr] || [];
              const today = isToday(day);
              const isDropTarget = dropIndicator?.dateStr === dateStr;
              return (
                <div
                  key={dateStr}
                  className={`relative border-l border-black/5 ${today ? 'bg-[#48293A]/[0.02]' : ''} ${isDropTarget ? 'bg-[#48293A]/[0.04]' : ''}`}
                  onDragOver={(e) => handleDragOver(e, dateStr)}
                  onDrop={(e) => handleDrop(e, dateStr)}
                  onDragLeave={handleDragLeave}
                >
                  {/* Hour grid lines */}
                  {HOURS.map(hour => (
                    <div
                      key={hour}
                      className="absolute left-0 right-0 border-t border-black/5"
                      style={{ top: (hour - START_HOUR) * HOUR_HEIGHT }}
                    />
                  ))}
                  {/* Half-hour lines */}
                  {HOURS.map(hour => (
                    <div
                      key={`h-${hour}`}
                      className="absolute left-0 right-0 border-t border-dashed border-black/[0.03]"
                      style={{ top: (hour - START_HOUR) * HOUR_HEIGHT + HOUR_HEIGHT / 2 }}
                    />
                  ))}
                  {/* Drop indicator */}
                  {isDropTarget && dropIndicator && (
                    <div
                      className="absolute left-1 right-1 h-0.5 bg-[#48293A] z-30 pointer-events-none rounded-full"
                      style={{ top: dropIndicator.y }}
                    >
                      <span className="absolute -top-3 left-0 text-[10px] font-bold text-[#48293A] bg-white px-1 rounded shadow-sm">
                        {dropIndicator.time}
                      </span>
                    </div>
                  )}
                  {/* Workshop blocks */}
                  {dayWorkshops.map(w => (
                    <WorkshopBlock
                      key={w.id}
                      workshop={w}
                      onClick={onSelectWorkshop}
                      isReserved={reservedIds.includes(w.id)}
                      draggable={draggable}
                      onDragStart={setDraggingWorkshop}
                    />
                  ))}
                </div>
              );
            })}

            {/* Current time indicator */}
            {days.some(d => isToday(d)) && (() => {
              const now = new Date();
              const h = now.getHours();
              const m = now.getMinutes();
              if (h < START_HOUR || h >= END_HOUR) return null;
              const top = (h - START_HOUR) * HOUR_HEIGHT + (m / 60) * HOUR_HEIGHT;
              const todayIdx = days.findIndex(d => isToday(d));
              return (
                <div
                  className="absolute h-0.5 bg-[#48293A] z-30 pointer-events-none"
                  style={{
                    top: `${top}px`,
                    left: `calc(60px + ${todayIdx} * ((100% - 60px) / 7))`,
                    width: `calc((100% - 60px) / 7)`,
                  }}
                >
                  <div className="absolute -left-1 -top-1 w-2.5 h-2.5 rounded-full bg-[#48293A]" />
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Mobile Day View */}
      <div className="sm:hidden px-4 py-4">
        {(() => {
          const dateStr = format(mobileDay, 'yyyy-MM-dd');
          const dayWorkshops = workshopsByDay[dateStr] || [];
          if (dayWorkshops.length === 0) {
            return (
              <div className="text-center py-12 text-manrique-muted">
                <CalendarDays size={32} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">{t('no_workshops_day')}</p>
              </div>
            );
          }
          return (
            <div className="space-y-3">
              {dayWorkshops.map(w => {
                const cat = CATEGORY_BG[w.category] || CATEGORY_BG.general;
                const isReserved = reservedIds.includes(w.id);
                return (
                  <div
                    key={w.id}
                    onClick={() => onSelectWorkshop?.(w)}
                    className="flex gap-3 p-4 rounded-2xl border border-black/5 bg-white cursor-pointer hover:shadow-md transition-all"
                    data-testid={`mobile-workshop-${w.id}`}
                  >
                    <div
                      className="w-1 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cat.border }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-heading font-bold text-sm text-manrique-black truncate">{w.title}</h4>
                        {isReserved && (
                          <span className="text-[10px] font-bold text-[#2B823A] bg-[#2B823A]/10 px-2 py-0.5 rounded-full flex-shrink-0">
                            Reservado
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-manrique-muted">
                        <span className="flex items-center gap-1">
                          <Clock size={11} /> {w.time_start} - {w.time_end}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={11} /> {w.current_count}/{w.max_capacity}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Empty state for desktop */}
      {weekWorkshopCount === 0 && (
        <div className="hidden sm:block text-center py-16 text-manrique-muted">
          <CalendarDays size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-heading font-bold text-base text-manrique-grey">{t('no_workshops_week')}</p>
          <p className="text-sm mt-1">{t('navigate_hint')}</p>
        </div>
      )}
    </div>
  );
}
